/**
 * LevNutri - Bioimpedância (OMRON HBF-514C + Consulta Online + Adipômetro + Relatório)
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { bioimpedanciaService, bioimpedanciaOnlineService, patientsService, authService } from '../../services/api';
import PrintEvolution, { PrintEvolutionCSS, BodyWithAnnotations } from './PrintEvolution';
import { Ico, emo, noEmo } from '../ui/Ico';

// ─── Constantes ───────────────────────────────────────────────────────────────

const INITIAL_FORM = {
  data_medicao: new Date().toISOString().split('T')[0],
  peso: '', gordura_corporal: '', imc: '', musculos_esqueleticos: '',
  gordura_visceral: '', metabolismo_basal: '', idade_corporal: '', observacoes: '',
};

const INITIAL_ONLINE = {
  peso: '', altura: '', cintura: '', quadril: '', pescoco: '', braco: '', panturrilha: '',
  data_medicao: new Date().toISOString().split('T')[0],
};

const DOBRAS_FIELDS = {
  triceps:      'Tríceps',
  subescapular: 'Subescapular',
  biceps:       'Bíceps',
  suprailiaca:  'Suprailíaca',
  abdominal:    'Abdominal',
  coxa:         'Coxa',
  panturrilha:  'Panturrilha',
  peitoral:     'Peitoral',
  axilar_media: 'Axilar Média',
};

const PROTOCOLS = [
  { id: 'pollock7f', label: 'Pollock 7-dobras (Feminino)', gender: 'F', fields: ['triceps','subescapular','suprailiaca','abdominal','coxa','axilar_media','peitoral'] },
  { id: 'pollock7m', label: 'Pollock 7-dobras (Masculino)', gender: 'M', fields: ['triceps','subescapular','suprailiaca','abdominal','coxa','axilar_media','peitoral'] },
  { id: 'pollock3f', label: 'Pollock 3-dobras (Feminino)', gender: 'F', fields: ['triceps','suprailiaca','coxa'] },
  { id: 'pollock3m', label: 'Pollock 3-dobras (Masculino)', gender: 'M', fields: ['peitoral','abdominal','coxa'] },
];

// ─── Cálculo de dobras cutâneas ───────────────────────────────────────────────

function calcBodyFat(protocol, dobras, age) {
  const sum = protocol.fields.reduce((acc, f) => acc + (parseFloat(dobras[f]) || 0), 0);
  if (sum <= 0) return null;
  let density;
  if (protocol.id === 'pollock7f' || protocol.id === 'pollock7m') {
    density = protocol.gender === 'F'
      ? 1.097 - 0.000469971 * sum + 0.00000056 * sum * sum - 0.00012828 * age
      : 1.112 - 0.000434 * sum + 0.0000003 * sum * sum - 0.000288 * age;
  } else if (protocol.id === 'pollock3f') {
    density = 1.0994921 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * age;
  } else {
    density = 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * age;
  }
  const gc = (4.95 / density - 4.5) * 100;
  return Math.min(Math.max(gc, 3), 60).toFixed(1);
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function ChartBlock({ title, data, dataKey, color, unit, small }) {
  return (
    <div style={{ marginBottom: small ? '0' : '1.5rem' }}>
      <h3 style={{ fontSize: '0.9rem', color: '#4a4440', marginBottom: '0.4rem' }}>{emo(title)}</h3>
      <ResponsiveContainer width="100%" height={small ? 150 : 200}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
          <XAxis dataKey="data_label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v) => [`${v}${unit}`, title]} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2}
            dot={{ fill: color, r: 3 }} activeDot={{ r: 5 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DualAxisChart({ data }) {
  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background:'#ffffff', border:'1px solid #d9d0c6', borderRadius:'8px', padding:'10px 14px', fontSize:'0.82rem', boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
        <p style={{ margin:'0 0 6px', fontWeight:600, color:'#5a3e2b' }}>{emo(label)}</p>
        {payload.map(p => (
          <p key={p.dataKey} style={{ margin:'2px 0', color: p.stroke }}>
            {p.name}: <strong>{p.value != null ? p.value : '—'}</strong>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '0.9rem', color: '#4a4440', marginBottom: '0.4rem' }}>Peso vs. Gordura Corporal</h3>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 24, left: -10, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
          <XAxis dataKey="data_label" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="peso" orientation="left" tick={{ fontSize: 10 }} unit=" kg" domain={['auto', 'auto']} />
          <YAxis yAxisId="gordura" orientation="right" tick={{ fontSize: 10 }} unit="%" domain={['auto', 'auto']} />
          <Tooltip content={customTooltip} />
          <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: '6px' }} />
          <Line yAxisId="peso" type="monotone" dataKey="peso" name="Peso (kg)" stroke="#D4A574" strokeWidth={2.5}
            dot={{ fill: '#D4A574', r: 4 }} activeDot={{ r: 6 }} connectNulls />
          <Line yAxisId="gordura" type="monotone" dataKey="gordura_corporal" name="Gordura (%)" stroke="#c4933f" strokeWidth={2.5}
            dot={{ fill: '#c4933f', r: 4 }} activeDot={{ r: 6 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Badge({ text, color }) {
  const colors = {
    online:     { bg: '#edf1f6', color: '#4f6a86' },
    presencial: { bg: '#edf4ee', color: '#46714f' },
  };
  const c = colors[color] || { bg: '#fbf8f5', color: '#736b64' };
  return (
    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', fontWeight: 600,
      background: c.bg, color: c.color, letterSpacing: '0.02em' }}>
      {emo(text)}
    </span>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

function Bioimpedancia({ patientId: initialPatientId, onNavigate }) {
  const [activeTab, setActiveTab]     = useState('historico');
  const [patients, setPatients]       = useState([]);
  const [patientId, setPatientId]     = useState(initialPatientId || '');
  const [patient, setPatient]         = useState(null);
  const [records, setRecords]         = useState([]);
  const [evolution, setEvolution]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  // Presencial form
  const [formData, setFormData]       = useState(INITIAL_FORM);
  const [editingId, setEditingId]     = useState(null);
  const [saving, setSaving]           = useState(false);

  // Adipômetro
  const [showAdipometro, setShowAdipometro] = useState(false);
  const [protocolId, setProtocolId]   = useState('pollock3f');
  const [dobras, setDobras]           = useState({});
  const [gcCalc, setGcCalc]           = useState(null);

  // Online tab
  const [onlineForm, setOnlineForm]   = useState(INITIAL_ONLINE);
  const [onlineResult, setOnlineResult] = useState(null);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineSaving, setOnlineSaving]   = useState(false);

  // Relatório
  const [motivation, setMotivation]   = useState('');
  const [motivLoading, setMotivLoading] = useState(false);
  const [showPrint, setShowPrint]     = useState(false);
  const [proximoRetorno, setProximoRetorno] = useState('');
  const [currentUser]                 = useState(() => authService.getCurrentUser());

  // ── Carregamentos ─────────────────────────────────────────────────────────

  useEffect(() => {
    patientsService.getAll().then(res => { if (res.success) setPatients(res.data); });
  }, []);

  useEffect(() => {
    if (!patientId) return;
    const p = patients.find(p => String(p.id) === String(patientId));
    setPatient(p || null);
    loadRecords(patientId);
    loadEvolution(patientId);
  }, [patientId, patients]);

  const loadRecords = async (pid) => {
    setLoading(true);
    try {
      const res = await bioimpedanciaService.getByPatient(pid);
      if (res.success) setRecords(res.data);
    } catch { setError('Erro ao carregar medições'); }
    finally { setLoading(false); }
  };

  const loadEvolution = async (pid) => {
    try {
      const res = await bioimpedanciaService.getEvolution(pid, 20);
      if (res.success) {
        const data = res.data;
        if (Array.isArray(data)) {
          setEvolution(data);
        } else {
          setEvolution(data.records || []);
          setProximoRetorno(data.proximo_retorno || '');
        }
      }
    } catch {}
  };

  // ── Adipômetro ────────────────────────────────────────────────────────────

  const protocol = PROTOCOLS.find(p => p.id === protocolId) || PROTOCOLS[0];

  const handleDobraChange = (field, value) => {
    const next = { ...dobras, [field]: value };
    setDobras(next);
    const age = patient ? (new Date().getFullYear() - new Date(patient.birth_date).getFullYear()) : 30;
    const gc = calcBodyFat(protocol, next, age);
    setGcCalc(gc);
    if (gc !== null) {
      setFormData(prev => ({ ...prev, gordura_corporal: gc }));
    }
  };

  const handleProtocolChange = (pid) => {
    setProtocolId(pid);
    setDobras({});
    setGcCalc(null);
  };

  // ── Formulário Presencial ─────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) { setError('Selecione um paciente'); return; }
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = { ...formData, patient_id: patientId, tipo_consulta: 'presencial' };
      if (showAdipometro && Object.keys(dobras).length > 0) {
        payload.dobras_cutaneas = dobras;
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = editingId
        ? await bioimpedanciaService.update(editingId, payload)
        : await bioimpedanciaService.create(payload);
      if (res.success) {
        setSuccessMsg(res.message || 'Salvo com sucesso!');
        setFormData(INITIAL_FORM); setDobras({}); setGcCalc(null);
        setEditingId(null); setShowAdipometro(false);
        loadRecords(patientId); loadEvolution(patientId);
        setActiveTab('historico');
      } else { setError(res.error || 'Erro ao salvar'); }
    } catch { setError('Erro ao salvar medição'); }
    finally { setSaving(false); }
  };

  const handleEdit = (record) => {
    setFormData({
      data_medicao:          record.data_medicao || INITIAL_FORM.data_medicao,
      peso:                  record.peso ?? '',
      gordura_corporal:      record.gordura_corporal ?? '',
      imc:                   record.imc ?? '',
      musculos_esqueleticos: record.musculos_esqueleticos ?? '',
      gordura_visceral:      record.gordura_visceral ?? '',
      metabolismo_basal:     record.metabolismo_basal ?? '',
      idade_corporal:        record.idade_corporal ?? '',
      observacoes:           record.observacoes ?? '',
    });
    if (record.dobras_cutaneas && typeof record.dobras_cutaneas === 'object') {
      setDobras(Object.fromEntries(Object.entries(record.dobras_cutaneas).map(([k,v]) => [k, String(v)])));
      setShowAdipometro(true);
    } else {
      setDobras({}); setShowAdipometro(false);
    }
    setEditingId(record.id);
    setActiveTab('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta medição?')) return;
    try {
      const res = await bioimpedanciaService.delete(id);
      if (res.success) { loadRecords(patientId); loadEvolution(patientId); }
    } catch { setError('Erro ao remover medição'); }
  };

  // ── Consulta Online ───────────────────────────────────────────────────────

  const handleOnlineChange = (e) => {
    const { name, value } = e.target;
    setOnlineForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEstimate = async () => {
    if (!patientId) { setError('Selecione um paciente'); return; }
    if (!onlineForm.peso || !onlineForm.altura) { setError('Peso e altura são obrigatórios'); return; }
    setOnlineLoading(true); setError(''); setOnlineResult(null);
    try {
      const res = await bioimpedanciaOnlineService.estimate({ ...onlineForm, patient_id: patientId });
      if (res.success) setOnlineResult(res.data);
      else setError(res.error || 'Erro na estimativa');
    } catch { setError('Erro ao conectar com a IA'); }
    finally { setOnlineLoading(false); }
  };

  const handleSaveOnline = async () => {
    if (!onlineResult) return;
    setOnlineSaving(true); setError('');
    try {
      const res = await bioimpedanciaOnlineService.estimate({
        ...onlineForm, patient_id: patientId, salvar: true,
      });
      if (res.success) {
        setSuccessMsg('Medição online salva com sucesso!');
        setOnlineForm(INITIAL_ONLINE); setOnlineResult(null);
        loadRecords(patientId); loadEvolution(patientId);
        setActiveTab('historico');
      } else { setError(res.error || 'Erro ao salvar'); }
    } catch { setError('Erro ao salvar medição'); }
    finally { setOnlineSaving(false); }
  };

  // ── Relatório ─────────────────────────────────────────────────────────────

  const handleMotivation = async () => {
    if (!patientId || records.length === 0) return;
    setMotivLoading(true);
    try {
      const first = records[records.length - 1];
      const last  = records[0];
      const res = await bioimpedanciaOnlineService.motivation({
        patient_id: patientId,
        sessions:   records.length,
        first:      { peso: first.peso, gordura_corporal: first.gordura_corporal },
        last:       { peso: last.peso,  gordura_corporal: last.gordura_corporal  },
      });
      if (res.success) setMotivation(res.data.mensagem || '');
    } catch {}
    finally { setMotivLoading(false); }
  };

  const chartData = [...evolution].reverse().map(r => ({
    ...r,
    data_label: new Date(r.data_medicao + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
  }));

  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      <PrintEvolutionCSS />
      {showPrint && (
        <PrintEvolution
          patient={patient}
          records={records}
          evolution={evolution}
          motivation={motivation}
          nutricionista={currentUser}
          proximoRetorno={proximoRetorno}
          onClose={() => setShowPrint(false)}
        />
      )}

      {/* Cabeçalho */}
      <div style={S.header}>
        <div>
          {onNavigate && (
            <button style={S.backBtn} onClick={() => onNavigate(initialPatientId ? 'patient-detail' : 'patients', initialPatientId)}>
              ← Voltar
            </button>
          )}
          <h1 style={S.title}><Ico e="⚖" sp />Bioimpedância</h1>
          <p style={S.subtitle}>OMRON HBF-514C — Composição Corporal</p>
        </div>
        {patientId && (
          <button style={S.primaryBtn} onClick={() => { setFormData(INITIAL_FORM); setEditingId(null); setActiveTab('form'); }}>
            + Nova Medição
          </button>
        )}
      </div>

      {/* Seletor de Paciente */}
      <div style={S.card}>
        <label style={S.label}>Paciente</label>
        <select style={S.select} value={patientId} onChange={e => setPatientId(e.target.value)}>
          <option value="">Selecione um paciente...</option>
          {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {error      && <div style={S.error}>{error}</div>}
      {successMsg && <div style={S.success}>{successMsg}</div>}

      {patientId && (
        <>
          {/* Tabs */}
          <div style={S.tabs}>
            {emo([
              { key: 'historico', label: '📋 Histórico' },
              { key: 'form',      label: editingId ? '✏️ Editar' : '➕ Presencial' },
              { key: 'online',    label: '🌐 Consulta Online' },
              { key: 'relatorio', label: '📊 Relatório' },
            ].map(tab => (
              <button key={tab.key}
                style={{ ...S.tab, ...(activeTab === tab.key ? S.tabActive : {}) }}
                onClick={() => setActiveTab(tab.key)}>
                {emo(tab.label)}
              </button>
            )))}
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB: HISTÓRICO                                              */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'historico' && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>
                Histórico de Medições
                {patient && <span style={S.badge}>{patient.name}</span>}
              </h2>
              {loading ? (
                <p style={S.emptyMsg}>Carregando...</p>
              ) : records.length === 0 ? (
                <div style={S.emptyState}>
                  <p>Nenhuma medição registrada ainda.</p>
                  <button style={S.primaryBtn} onClick={() => setActiveTab('form')}>+ Registrar primeira medição</button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {['Data','Tipo','Peso','Gord.','IMC','Músc.','G.Visc.','Met. Basal','Idade Corp.',''].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(r => (
                        <tr key={r.id} style={S.tr}>
                          <td style={S.td}>{formatDate(r.data_medicao)}</td>
                          <td style={S.td}><Badge text={r.tipo_consulta === 'online' ? 'Online' : 'Presencial'} color={r.tipo_consulta === 'online' ? 'online' : 'presencial'} /></td>
                          <td style={S.td}>{r.peso != null ? `${r.peso} kg` : '-'}</td>
                          <td style={S.td}>{r.gordura_corporal != null ? `${r.gordura_corporal}%` : '-'}</td>
                          <td style={S.td}>{r.imc ?? '-'}</td>
                          <td style={S.td}>{r.musculos_esqueleticos != null ? `${r.musculos_esqueleticos}%` : '-'}</td>
                          <td style={S.td}>{r.gordura_visceral ?? '-'}</td>
                          <td style={S.td}>{r.metabolismo_basal != null ? `${r.metabolismo_basal} kcal` : '-'}</td>
                          <td style={S.td}>{r.idade_corporal != null ? `${r.idade_corporal} anos` : '-'}</td>
                          <td style={S.td}>
                            <button style={S.iconBtn} onClick={() => handleEdit(r)}><Ico e="✏" /></button>
                            <button style={S.iconBtn} onClick={() => handleDelete(r.id)}><Ico e="🗑" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB: PRESENCIAL                                             */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'form' && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>
                {editingId ? 'Editar Medição' : 'Nova Medição Presencial'}
                {patient && <span style={S.badge}>{patient.name}</span>}
              </h2>
              <form onSubmit={handleSubmit}>
                <div style={S.grid2}>
                  <div style={S.field}>
                    <label style={S.label}>Data da Medição *</label>
                    <input type="date" name="data_medicao" required style={S.input}
                      value={formData.data_medicao} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Peso (kg) *</label>
                    <input type="number" name="peso" required step="0.1" min="0" style={S.input}
                      placeholder="Ex: 72.5" value={formData.peso} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Gordura Corporal (%)</label>
                    <input type="number" name="gordura_corporal" step="0.1" min="0" max="100" style={S.input}
                      placeholder="Ex: 24.5" value={formData.gordura_corporal} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>IMC</label>
                    <input type="number" name="imc" step="0.1" min="0" style={S.input}
                      placeholder="Ex: 23.8" value={formData.imc} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Músculos Esqueléticos (%)</label>
                    <input type="number" name="musculos_esqueleticos" step="0.1" min="0" max="100" style={S.input}
                      placeholder="Ex: 35.2" value={formData.musculos_esqueleticos} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Gordura Visceral (nível 1–30)</label>
                    <input type="number" name="gordura_visceral" step="1" min="1" max="30" style={S.input}
                      placeholder="Ex: 8" value={formData.gordura_visceral} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Metabolismo Basal (kcal)</label>
                    <input type="number" name="metabolismo_basal" step="1" min="0" style={S.input}
                      placeholder="Ex: 1540" value={formData.metabolismo_basal} onChange={handleChange} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Idade Corporal (anos)</label>
                    <input type="number" name="idade_corporal" step="1" min="1" max="120" style={S.input}
                      placeholder="Ex: 35" value={formData.idade_corporal} onChange={handleChange} />
                  </div>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Observações</label>
                  <textarea name="observacoes" rows={2} style={S.textarea}
                    placeholder="Notas adicionais..." value={formData.observacoes} onChange={handleChange} />
                </div>

                {/* ── Adipômetro ── */}
                <div style={{ marginTop: '1.25rem' }}>
                  <button type="button" style={S.toggleBtn} onClick={() => setShowAdipometro(v => !v)}>
                    {showAdipometro ? '▲' : '▼'} Adipômetro / Dobras Cutâneas (opcional)
                  </button>

                  {showAdipometro && (
                    <div style={{ ...S.card, marginTop: '0.75rem', background: '#fbf8f5' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={S.label}>Protocolo</label>
                        <select style={{ ...S.select, maxWidth: '340px' }} value={protocolId} onChange={e => handleProtocolChange(e.target.value)}>
                          {PROTOCOLS.map(p => <option key={p.id} value={p.id}>{noEmo(p.label)}</option>)}
                        </select>
                      </div>

                      <div style={S.grid3}>
                        {protocol.fields.map(f => (
                          <div key={f} style={S.field}>
                            <label style={S.label}>{emo(DOBRAS_FIELDS[f])} (mm)</label>
                            <input type="number" step="0.5" min="0" max="80" style={S.input}
                              placeholder="mm" value={dobras[f] || ''}
                              onChange={e => handleDobraChange(f, e.target.value)} />
                          </div>
                        ))}
                      </div>

                      {gcCalc !== null && (
                        <div style={S.gcResult}>
                          <span style={{ fontWeight: 600, fontSize: '1.1rem', color: '#D4A574' }}>{gcCalc}%</span>
                          <span style={{ color: '#736b64', fontSize: '0.85rem' }}> gordura corporal estimada ({emo(protocol.label)}) — preenchido automaticamente no campo acima</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={S.formActions}>
                  <button type="button" style={S.secondaryBtn}
                    onClick={() => { setFormData(INITIAL_FORM); setEditingId(null); setDobras({}); setGcCalc(null); setShowAdipometro(false); setActiveTab('historico'); }}>
                    Cancelar
                  </button>
                  <button type="submit" style={S.primaryBtn} disabled={saving}>
                    {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Registrar')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB: CONSULTA ONLINE                                        */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'online' && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>
                <Ico e="🌐" sp />Consulta Online — Estimativa por IA
                {patient && <span style={S.badge}>{patient.name}</span>}
              </h2>
              <p style={{ color: '#736b64', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                Informe as medidas corporais e a IA estimará os 7 indicadores OMRON usando equações validadas (Deurenberg, US Navy, Cunningham).
              </p>

              <div style={S.grid2}>
                <div style={S.field}>
                  <label style={S.label}>Data da Medição</label>
                  <input type="date" name="data_medicao" style={S.input} value={onlineForm.data_medicao} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Peso (kg) *</label>
                  <input type="number" name="peso" step="0.1" min="0" style={S.input} placeholder="Ex: 68.0" value={onlineForm.peso} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Altura (cm) *</label>
                  <input type="number" name="altura" step="0.5" min="100" max="220" style={S.input} placeholder="Ex: 165" value={onlineForm.altura} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Cintura (cm)</label>
                  <input type="number" name="cintura" step="0.5" min="0" style={S.input} placeholder="Ex: 80" value={onlineForm.cintura} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Quadril (cm)</label>
                  <input type="number" name="quadril" step="0.5" min="0" style={S.input} placeholder="Ex: 96" value={onlineForm.quadril} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Pescoço (cm)</label>
                  <input type="number" name="pescoco" step="0.5" min="0" style={S.input} placeholder="Ex: 33" value={onlineForm.pescoco} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Braço relaxado (cm)</label>
                  <input type="number" name="braco" step="0.5" min="0" style={S.input} placeholder="Ex: 29" value={onlineForm.braco} onChange={handleOnlineChange} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Panturrilha (cm)</label>
                  <input type="number" name="panturrilha" step="0.5" min="0" style={S.input} placeholder="Ex: 35" value={onlineForm.panturrilha} onChange={handleOnlineChange} />
                </div>
              </div>

              <div style={{ textAlign: 'right', marginTop: '1rem' }}>
                <button style={S.primaryBtn} onClick={handleEstimate} disabled={onlineLoading}>
                  {emo(onlineLoading ? '⏳ Estimando...' : '✨ Estimar com IA')}
                </button>
              </div>

              {/* Resultado */}
              {onlineResult && (
                <div style={{ marginTop: '1.5rem', borderTop: '1.5px solid #d9d0c6', paddingTop: '1.25rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2f2a26', marginBottom: '1rem' }}>
                    Resultado Estimado pela IA
                  </h3>
                  <div style={S.resultGrid}>
                    {[
                      { label: 'Gordura Corporal', value: onlineResult.gordura_corporal, unit: '%', warn: v => v > 30 },
                      { label: 'IMC', value: onlineResult.imc, unit: '', warn: v => v > 25 || v < 18.5 },
                      { label: 'Músculos Esq.', value: onlineResult.musculos_esqueleticos, unit: '%', warn: v => v < 30 },
                      { label: 'Gordura Visceral', value: onlineResult.gordura_visceral, unit: ' nív.', warn: v => v > 9 },
                      { label: 'Metabolismo Basal', value: onlineResult.metabolismo_basal, unit: ' kcal', warn: () => false },
                      { label: 'Idade Corporal', value: onlineResult.idade_corporal, unit: ' anos', warn: () => false },
                      { label: 'Massa de Gordura', value: onlineResult.peso_gordura, unit: ' kg', warn: () => false },
                      { label: 'Massa Magra', value: onlineResult.peso_magro, unit: ' kg', warn: () => false },
                    ].map(({ label, value, unit, warn }) => (
                      <div key={label} style={{ ...S.resultCard, borderLeft: `3px solid ${warn(value) ? '#e8c9a8' : '#b9d2bd'}` }}>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#736b64' }}>{emo(label)}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '1.3rem', fontWeight: 600, color: warn(value) ? '#a8483f' : '#46714f' }}>
                          {value != null ? `${value}${unit}` : '-'}
                        </p>
                      </div>
                    ))}
                  </div>

                  {onlineResult.observacao_ia && (
                    <p style={{ fontSize: '0.8rem', color: '#736b64', fontStyle: 'italic', marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fbf8f5', borderRadius: '6px' }}>
                      <Ico e="ℹ" /> {onlineResult.observacao_ia}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button style={S.secondaryBtn} onClick={() => setOnlineResult(null)}>Descartar</button>
                    <button style={S.primaryBtn} onClick={handleSaveOnline} disabled={onlineSaving}>
                      {emo(onlineSaving ? 'Salvando...' : '💾 Salvar esta medição')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB: RELATÓRIO                                              */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'relatorio' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ ...S.cardTitle, margin: 0 }}>
                  <Ico e="📊" sp />Relatório de Evolução
                  {patient && <span style={S.badge}>{patient.name}</span>}
                </h2>
                {records.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button style={S.secondaryBtn} onClick={handleMotivation} disabled={motivLoading}>
                      {emo(motivLoading ? '⏳ Gerando...' : '✨ Mensagem IA')}
                    </button>
                    <button style={S.primaryBtn} onClick={() => { setShowPrint(true); setTimeout(() => window.print(), 300); }}>
                      <Ico e="🖨" sp />Imprimir
                    </button>
                  </div>
                )}
              </div>

              {records.length < 1 ? (
                <div style={S.card}>
                  <p style={S.emptyMsg}>Nenhuma medição registrada ainda.</p>
                </div>
              ) : (
                <>
                  {/* Silhueta + indicadores */}
                  <div style={S.card}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2f2a26', marginBottom: '1.25rem', textAlign: 'center' }}>
                      {records.length === 1 ? 'Avaliação Corporal Inicial' : 'Composição Corporal — Última Medição'}
                    </h3>
                    <BodyWithAnnotations
                      gender={patient?.gender || 'F'}
                      last={records[0]}
                      first={records[records.length - 1]}
                    />
                  </div>

                  {/* Mensagem motivacional */}
                  {motivation && (
                    <div style={{ ...S.card, background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)', borderLeft: '4px solid #D4A574' }}>
                      <p style={{ margin: 0, fontSize: '0.95rem', fontStyle: 'italic', color: '#9a6a42', lineHeight: 1.6 }}>
                        "{motivation}"
                      </p>
                    </div>
                  )}

                  {/* Próximo retorno (tela) */}
                  {proximoRetorno && (
                    <div style={{ ...S.card, textAlign: 'center', borderTop: '3px solid #D4A574' }}>
                      <p style={{ margin: '0 0 4px', fontSize: '0.8rem', color: '#736b64', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Próximo Retorno Sugerido</p>
                      <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#2f2a26' }}>{proximoRetorno}</p>
                    </div>
                  )}

                  {/* Gráficos interativos (tela) — apenas 2+ medições */}
                  {records.length >= 2 && (
                    <div style={S.card}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2f2a26', marginBottom: '1rem' }}>Evolução ao longo do tempo</h3>
                      <DualAxisChart data={chartData} />
                      <ChartBlock title="Peso (kg)" data={chartData} dataKey="peso" color="#D4A574" unit=" kg" />
                      <ChartBlock title="Gordura Corporal (%)" data={chartData} dataKey="gordura_corporal" color="#e8c9a8" unit="%" />
                      <ChartBlock title="Músculos Esqueléticos (%)" data={chartData} dataKey="musculos_esqueleticos" color="#7189a6" unit="%" />
                      <div style={S.grid2}>
                        <ChartBlock title="IMC" data={chartData} dataKey="imc" color="#b9d2bd" unit="" small />
                        <ChartBlock title="Gordura Visceral" data={chartData} dataKey="gordura_visceral" color="#e3b3ad" unit="" small />
                        <ChartBlock title="Metabolismo Basal (kcal)" data={chartData} dataKey="metabolismo_basal" color="#b8c7d8" unit=" kcal" small />
                        <ChartBlock title="Idade Corporal (anos)" data={chartData} dataKey="idade_corporal" color="#b8c7d8" unit=" anos" small />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const S = {
  page:        { padding: '2rem', maxWidth: '1100px', margin: '0 auto' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title:       { fontSize: '1.75rem', fontWeight: 600, color: '#2f2a26', margin: '0 0 0.25rem' },
  subtitle:    { color: '#736b64', fontSize: '0.9rem', margin: 0 },
  backBtn:     { background: 'none', border: 'none', color: '#D4A574', cursor: 'pointer', fontSize: '0.9rem', padding: '0 0 0.5rem', display: 'block' },
  card:        { background: '#ffffff', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(44,44,44,0.07)' },
  cardTitle:   { fontSize: '1.1rem', fontWeight: 600, color: '#2f2a26', marginTop: 0, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  badge:       { fontSize: '0.8rem', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', borderRadius: '20px', padding: '2px 12px', fontWeight: 500 },
  label:       { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a4440', marginBottom: '0.4rem' },
  select:      { width: '100%', maxWidth: '400px', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', background: '#ffffff', outline: 'none' },
  input:       { width: '100%', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', boxSizing: 'border-box', outline: 'none' },
  textarea:    { width: '100%', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  grid2:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: '1rem', marginBottom: '1rem' },
  grid3:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: '0.75rem', marginBottom: '0.75rem' },
  field:       { display: 'flex', flexDirection: 'column' },
  formActions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  primaryBtn:  { background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '0.7rem 1.75rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  secondaryBtn:{ background: '#f6f0ea', color: '#4a4440', border: 'none', borderRadius: '8px', padding: '0.7rem 1.5rem', fontSize: '0.95rem', cursor: 'pointer' },
  toggleBtn:   { background: 'none', border: '1.5px dashed #d4a574', color: '#9a6a42', borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 },
  tabs:        { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  tab:         { padding: '0.6rem 1.25rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', background: '#ffffff', color: '#736b64', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
  tabActive:   { background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', borderColor: 'transparent' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th:          { padding: '0.75rem 0.75rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '2px solid #d9d0c6', fontWeight: 600, color: '#4a4440', whiteSpace: 'nowrap' },
  td:          { padding: '0.65rem 0.75rem', borderBottom: '1px solid #ebe3da', color: '#2f2a26', whiteSpace: 'nowrap' },
  tr:          {},
  iconBtn:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginRight: '0.25rem' },
  emptyMsg:    { color: '#736b64', textAlign: 'center', padding: '2rem 0' },
  emptyState:  { textAlign: 'center', padding: '2.5rem 0', color: '#736b64' },
  error:       { background: '#f8ebe9', color: '#a8483f', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
  success:     { background: '#edf4ee', color: '#46714f', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
  gcResult:    { background: '#fbf8f5', border: '1.5px solid #e8c9a8', borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  resultGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: '0.75rem' },
  resultCard:  { background: '#fbf8f5', borderRadius: '8px', padding: '0.75rem 1rem' },
};

export default Bioimpedancia;
